import { sortBy } from 'utils/array';

export type TokenInfo = Readonly<{
    /**
     * The index of where the token starts.
     */
    index: number;
    /**
     * The text of the token.
     */
    text: string;
    /**
     * If the token is a match contained in the supplied terms.
     */
    isMatch: boolean;
}>;

function splitTextWhereContainsTokens(text: string, lowerTerms: string[]): TokenInfo[] {
    const lowerText = text.toLowerCase();

    const ranges = lowerTerms
        .map((term) => {
            const start = lowerText.indexOf(term);
            const end = start + term.length - 1;
            return { term, start, end };
        })
        .filter((range) => range.start !== -1);

    if (ranges.length === 0) {
        return [{ text, isMatch: false, index: 0 }];
    }

    const { token, tokens, matchMode, index } = text.split('').reduce<{
        index: number;
        tokens: TokenInfo[];
        token: string;
        matchMode: boolean;
    }>(
        (res, char, charIndex) => {
            const range = ranges.find((r) => charIndex >= r.start && charIndex <= r.end);
            const rangeFound = range !== undefined;

            if (rangeFound === res.matchMode) {
                return { ...res, token: res.token + char };
            }
            if (res.token.length > 0) {
                return {
                    index: charIndex,
                    tokens: [
                        ...res.tokens,
                        { index: res.index, text: res.token, isMatch: res.matchMode },
                    ],
                    token: char,
                    matchMode: rangeFound,
                };
            }
            return { ...res, token: char, matchMode: rangeFound };
        },
        {
            index: 0,
            tokens: [],
            token: '',
            matchMode: false,
        },
    );

    return token.length > 0
        ? [...tokens, { index, text: token, isMatch: matchMode }]
        : tokens;
}

function splitTextWhereStartsWithTokens(text: string, lowerTerms: string[]): TokenInfo[] {
    const lowerText = text.toLowerCase();

    const sortedLowerTerms = sortBy(lowerTerms, {
        key: (term) => term.length,
        dir: 'desc',
    });

    const match = sortedLowerTerms.find((term) => lowerText.startsWith(term));

    if (!match) {
        return [{ index: 0, text, isMatch: false }];
    }
    if (match.length === text.length) {
        return [{ index: 0, text, isMatch: true }];
    }
    return [
        { index: 0, text: text.substring(0, match.length), isMatch: true },
        { index: match.length, text: text.substring(match.length), isMatch: false },
    ];
}

export type SplitTextOnTokensMode = 'startsWith' | 'contains';

export type SplitTextOnTokensOptions = Readonly<{
    text: string;
    terms: string[];
    mode: SplitTextOnTokensMode;
}>;

/**
 * Splits the supplied text into tokens using the supplied terms.
 *
 * @example
 *
 * const results = splitTextOnTokens({
 *   text: 'Foo Bar Baz Zip Bin',
 *   terms: ['bar baz', 'baz zip'],
 *   mode: 'contains'
 * });
 *
 * expect(results).toEqual([
 *     { index: 0, text: 'Foo ', isMatch: false },
 *     { index: 4, text: 'Bar Baz Zip', isMatch: true },
 *     { index: 15, text: ' Bin', isMatch: false },
 * ]);
 * @example
 *
 * const results = splitTextOnTokens({
 *   text: 'Foo Bar Baz Zip Bin',
 *   terms: ['for bar', 'for bar baz'],
 *   mode: 'startsWith'
 * });
 *
 * expect(results).toEqual([
 *     { index: 0, text: 'Foo Bar Baz', isMatch: true },
 *     { index: 11, text: ' Zip Bin', isMatch: false },
 * ]);
 */
export function splitTextOnTokens({
    text,
    terms,
    mode,
}: SplitTextOnTokensOptions): TokenInfo[] {
    if (text.length === 0) {
        return [];
    }
    if (terms.length === 0) {
        return [{ index: 0, text, isMatch: false }];
    }
    const lowerTerms = terms.map((term) => term.toLowerCase());

    switch (mode) {
        case 'contains':
            return splitTextWhereContainsTokens(text, lowerTerms);
        case 'startsWith':
            return splitTextWhereStartsWithTokens(text, lowerTerms);
        default:
            throw new Error('Invalid split mode');
    }
}

const serialNumberRegex = /[0-9a-f]{8}/i;
export function isValidSerialNumber(serialNumber: string) {
    return serialNumber.length === 8 && serialNumberRegex.test(serialNumber);
}

const macAddressRegex = /([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})/;
export function isValidMacAddress(macAddress: string | undefined) {
    return macAddress && macAddress.length === 17 && macAddressRegex.test(macAddress);
}

/**
 * Returns `true` if the supplied string is not `null`, `undefined`, or an empty string (`''`).
 *
 * @param value the value to check for null, undefined, or empty
 * @param trimValue if the value should be trimmed before checking (default to `false`)
 */
export const hasNonEmptyValue = (
    value: string | null | undefined,
    trimValue = false,
): value is string =>
    value !== null && value !== undefined && (trimValue ? value.trim() : value) !== '';

/**
 * Capitalizes the first letter in the given string
 * @param str The string whose first letter needs to be capitalized
 * @returns The supplied string with the first letter capitalized
 *
 * @example
 * "this is a string" => "This is a string"
 * "hEllo World" => "HEllo World"
 */
export const capitalizeFirstLetter = (str: string): string =>
    str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Capitalizes each word in the given string
 * @param str The string where each word is to be capitalized
 * @returns The supplied string with each letter capitalized
 *
 * @example
 * "this is a string" => "This Is A String"
 */
export const capitalizeEachWord = (str: string): string =>
    str.replace(/\b\w/g, (match) => match.toUpperCase());

/**
 * Returns undefined if the string is an empty string or spaces
 * @param str The string
 * @returns Undefined if the string is empty, or the string
 */
export const undefinedIfEmpty = (str: string | undefined): string | undefined =>
    !str?.trim() ? undefined : str;

const insertSpacesRegex = /([a-z]|[A-Z]+)(?=[A-Z0-9])/g;
/**
 * Inserts spaces at word boundaries for a PascalCase or camelCase input string
 */
export const insertSpacesOnCaseChange = (str: string) =>
    str.replaceAll(insertSpacesRegex, '$1 ');

/**
 * Pluralizes a word if the given count is not 1. By default, just appends an 's' to `singularText`.
 * @param count The count of items
 * @param singularText The text for the singular item
 * @param pluralText Optionally, override the default behavior by specifying the plural form of the item
 * @returns The pluralized item text
 */
export const pluralize = (count: number, singularText: string, pluralText?: string) =>
    count !== 1 ? (pluralText ?? `${singularText}s`) : singularText;

/**
 * A function to ellipsize (...) text
 *
 * Should generally not be used in HTML as css should be preferred, but may be
 * used where css is not fully supported, like in SVG elements
 */
export function ellipsize(maxTotalLen: number, text: string): string {
    return text.length <= maxTotalLen ? text : text.substring(0, maxTotalLen - 3) + '...';
}

/**
 * Gets pluralized item count text.
 * @param count The count of items
 * @param singularText The text for the singular item
 * @param pluralText Optionally, override the default behavior by specifying the plural form of the item
 * @returns The pluralized item text with count.
 *
 * @example
 *  getItemCountText(3, 'issue') => '3 issues'
 *  getItemCountText(1, 'indicator') => '1 indicator'
 *  getItemCountText(4, 'foot', 'feet') => '4 feet'
 */
export const getItemCountText = (
    count: number,
    singularText: string,
    pluralText?: string,
) => `${count} ${pluralize(count, singularText, pluralText)}`;
